import type { Matter } from "@/lib/types";
import {
  retrieveMatterEventsQueryOptions,
  retrieveMatterTasksQueryOptions,
} from "@/lib/data/react-query";
import { useTasksViewModel } from "@/hooks/matter-view-models/use-tasks-view-model";
import { cn } from "@/lib/utils/cn";
import { formatDate, formatUsersName } from "@/lib/utils/format";
import dayjs from "dayjs";
import { Clock3 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  formatDueBadge,
  getInitials,
  getSidebarTaskTone,
  getTaskAssignee,
  getUserConnectionDisplayName,
} from "../matter-overview-utils";
import { SectionAction } from "./SectionAction";

export function OverviewSectionSidebar({ matter }: { matter: Matter }) {
  const { data: eventLogsResponse } = useQuery(
    retrieveMatterEventsQueryOptions({ matterId: matter.id, pageSize: 4 }),
  );
  const { data: tasksResponse } = useQuery(
    retrieveMatterTasksQueryOptions({ matterId: matter.id }),
  );
  const { allTasks } = useTasksViewModel(matter, tasksResponse?.data);
  const upcomingTasks = [...allTasks]
    .filter((task) => task.status !== "Completed")
    .sort(
      (a, b) =>
        dayjs(a.dueDate ?? a.dateCreated).valueOf() -
        dayjs(b.dueDate ?? b.dateCreated).valueOf(),
    )
    .slice(0, 5)
    .map((task) => {
      const assignee = getTaskAssignee(matter, task);
      const assigneeName = getUserConnectionDisplayName(assignee);
      const dueDate = task.dueDate ? dayjs(task.dueDate) : null;
      const daysUntilDue =
        dueDate && dueDate.isValid()
          ? dueDate.startOf("day").diff(dayjs().startOf("day"), "day")
          : null;

      return {
        id: task.id,
        title: task.title,
        assigneeName,
        assigneeInitials: getInitials(assigneeName),
        dueBadge: formatDueBadge(daysUntilDue),
        dueTone: getSidebarTaskTone(daysUntilDue),
      };
    });

  const recentActivity = [...(eventLogsResponse?.data ?? [])]
    .sort(
      (a, b) => dayjs(b.dateCreated).valueOf() - dayjs(a.dateCreated).valueOf(),
    )
    .slice(0, 4)
    .map((event) => ({
      id: event.id,
      initials: getInitials(formatUsersName(event.actor)),
      message: event.message,
      timestamp: formatDate(event.dateCreated, false),
    }));

  return (
    <aside className="space-y-4">
      <div className="bg-card overflow-hidden rounded-[20px] border">
        <div className="border-b px-4 py-3">
          <p className="text-foreground text-sm font-semibold">
            Team &amp; activity
          </p>
        </div>

        <div className="border-b px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-muted-foreground text-[10px] font-semibold">
              Upcoming tasks
            </p>
            <SectionAction
              to="/matters/$matterId/tasks"
              params={{ matterId: matter.id }}
              label="View all"
            />
          </div>
          <div className="mt-3 space-y-4">
            {upcomingTasks.length > 0 ? (
              upcomingTasks.map((task) => (
                <div key={task.id} className="flex items-start gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-[11px] font-semibold text-blue-700">
                    {task.assigneeInitials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-foreground text-[14px] leading-5 font-medium">
                        {task.title}
                      </p>
                      {task.dueBadge ? (
                        <span
                          className={cn(
                            "inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-[11px]",
                            task.dueTone === "red" && "bg-red-50 text-red-600",
                            task.dueTone === "yellow" &&
                              "bg-amber-50 text-amber-600",
                            task.dueTone === "gray" &&
                              "bg-slate-100 text-slate-500",
                            task.dueTone === "neutral" &&
                              "bg-slate-100 text-slate-500",
                          )}
                        >
                          <Clock3 className="h-3 w-3" />
                          {task.dueBadge}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {task.assigneeName}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">
                No open tasks tracked.
              </p>
            )}
          </div>
        </div>

        <div className="px-4 py-4">
          <p className="text-muted-foreground text-[10px] font-semibold">
            Recent activity
          </p>
          <div className="mt-3 space-y-4">
            {recentActivity.length > 0 ? (
              recentActivity.map((event) => (
                <div key={event.id} className="flex items-start gap-3">
                  <div className="bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold">
                    {event.initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground text-sm leading-snug">
                      {event.message}
                    </p>
                  </div>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {event.timestamp}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">
                No recent activity tracked.
              </p>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
